import { type KeyboardEvent, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Gift, MessageSquareShare, Settings } from 'lucide-react';

import { useBreakpointDown } from '@nesso/ui/hooks/use-breakpoint-down';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuPortal,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@nesso/ui/primitives/dropdown-menu';
import { useTheme } from '@nesso/ui/providers/theme-provider';

import { practiceMemberOptions } from '@/entities/practice-member';
import { isReferralEligible, subscriptionMeOptions } from '@/entities/subscription';
import { useAuth } from '@/entities/user';
import { ShareReferralLinkDialog } from '@/features/referral/share-link';
import { SUPPORT_EMAIL } from '@/shared/constants/support';

import { UserAvatar } from './user-dropdown/user-avatar';
import { UserDropdownActionItem } from './user-dropdown/user-dropdown-action-item';
import { UserDropdownFooterLinks } from './user-dropdown/user-dropdown-footer-links';
import { UserDropdownInfo } from './user-dropdown/user-dropdown-info';
import { UserDropdownThemeSelector } from './user-dropdown/user-dropdown-theme-selector';

export function UserDropdown() {
    const navigate = useNavigate();
    const isMobile = useBreakpointDown('md');
    const { data: practiceMember } = useQuery(practiceMemberOptions());
    const { data: subscription } = useQuery(subscriptionMeOptions());
    const { theme, setTheme } = useTheme();
    const { signOut } = useAuth();
    const [isReferralOpen, setIsReferralOpen] = useState(false);

    const handleSignOut = () => {
        void signOut();
    };

    // allows tabulation through the dropdown menu items from navbar trigger
    const tabulateThroughItems = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key !== 'Tab') return;
        e.preventDefault();
        const container = e.currentTarget as HTMLElement;
        const items = Array.from(
            container.querySelectorAll<HTMLElement>(
                '[data-radix-collection-item]:not([data-disabled])',
            ),
        );
        if (items.length === 0) return;
        const active = document.activeElement as HTMLElement | null;
        const currentIndex = active ? items.indexOf(active) : -1;
        const direction = e.shiftKey ? -1 : 1;
        const nextIndex = (currentIndex + direction + items.length) % items.length;
        items[nextIndex]?.focus();
    };

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        aria-label="User menu"
                    >
                        <UserAvatar name={practiceMember?.preferredFirstName} />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuPortal>
                    <DropdownMenuContent
                        className="min-w-65 p-0"
                        loop
                        sideOffset={5}
                        align="end"
                        onKeyDown={tabulateThroughItems}
                    >
                        {practiceMember?.preferredFirstName && practiceMember?.email && (
                            <UserDropdownInfo
                                name={practiceMember.preferredFirstName}
                                email={practiceMember.email}
                            />
                        )}
                        <DropdownMenuSeparator />
                        <UserDropdownActionItem
                            icon={Settings}
                            label="Settings"
                            onClick={() => {
                                void navigate({ to: isMobile ? '/settings' : '/settings/profile' });
                            }}
                        />
                        <UserDropdownActionItem
                            icon={MessageSquareShare}
                            label="Feedback"
                            onClick={() => {
                                window.location.href = `mailto:${SUPPORT_EMAIL}`;
                            }}
                        />
                        {isReferralEligible(subscription) && (
                            <UserDropdownActionItem
                                icon={Gift}
                                label="Earn a free month"
                                onClick={() => setIsReferralOpen(true)}
                            />
                        )}
                        <DropdownMenuSeparator />
                        <UserDropdownThemeSelector
                            theme={theme}
                            onThemeChange={(value: string) => {
                                if (value) setTheme(value as typeof theme);
                            }}
                        />
                        <DropdownMenuSeparator />
                        <UserDropdownActionItem
                            label="Help center"
                            onClick={() => {
                                window.open(
                                    'https://support.nessoapp.com',
                                    '_blank',
                                    'noopener,noreferrer',
                                );
                            }}
                        />
                        <UserDropdownActionItem label="Sign out" onClick={handleSignOut} />
                        <DropdownMenuSeparator />
                        <UserDropdownFooterLinks />
                    </DropdownMenuContent>
                </DropdownMenuPortal>
            </DropdownMenu>
            <ShareReferralLinkDialog open={isReferralOpen} onOpenChange={setIsReferralOpen} />
        </>
    );
}
